import { api } from "@/api/client";
import { Company } from "../company";
import { convertToURLSearchParams } from "@/util";
import { PaginatedSearch } from "@/types";
import { QualifierUser } from "../user";
import { Region } from "./region";

export enum WelderStatus {
  Inactive,
  Active,
  Deleted,
}

export enum QualificationStatus {
  NotFound,
  Ineligible,
  NoQualifications,
  Current,
  SomeExpired,
  AllExpired,
  SomeRevoked,
  AllRevoked,
}

export enum TestResult {
  Accept,
  Reject,
}

export type Welder = {
  welderId: string;
  welderVersionId: string;
  name: string;
  firstName: string;
  lastName: string;
  badgeNumber?: string;
  stampNumber?: string;
  company?: Company;
  status: WelderStatus;
  ssn: string;
  emailAddress?: string;
  phoneNumber?: string;
  weldingSpecialist?: QualifierUser;
  shopField?: string;
  regions: Region[];
  advisories?: string[];
  creationDate?: Date;
  ineligibleDate?: Date;
  welderNote?: string;
};

export type WelderListView = {
  id: string;
  name: string;
  badgeNumber?: string;
  ssn?: string;
  status: string;
  regions?: string;
  company?: string;
  shopField?: string;
  pinned: boolean;
};

export interface WelderFilters extends PaginatedSearch {
  favorite: boolean;
  projectId: string;
  contractorId: string;
  searchString: string;
}

export type WelderValidation = {
  isWelderUnique: boolean;
};

export type WelderExpirations = {
  welderId: string;
  basic?: string;
  cfR192?: string;
  cfR195?: string;
  basicNde?: string;
};

export type WelderAssociations = {
  inspectors: QualifierUser[];
  specialists: QualifierUser[];
  favorited: QualifierUser[];
};

export type WelderMetrics = {
  welderTests: number;
  api1104Tests: number;
  asmeTests: number;
  inServiceTests: number;
  testsPassed: number;
  testsFailed: number;
  passRate: number;
};

export type WelderUpdate = {
  updateId: string;
  processName: string;
  processType: string;
  ndeMethod: string;
  testType: string;
  acceptanceCriteria: string;
  result: TestResult;
  weldDate?: Date;
  ndeDate?: Date;
  hasAttachments: boolean;
};

export type WelderPicture = {
  pictureId: string;
  data: string;
};

export type WelderQualificationStatus = {
  status: QualificationStatus;
};

export type WelderStatusSearch = {
  wpsCode?: string;
  continuityType?: string;
};

export type WelderProcess = {
  processId: string;
  processName: string;
  processType: string;
  standard: string;
  status: string;
  revoked: boolean;
  revocationId?: string;
  revokedReason?: string;
  retestDate?: string;
};

export type WelderStatusHistory = {
  id: string;
  event: string;
  description: string;
  reason: string;
  date: Date;
  userName: string;
};

export function getWelderList(filters: Partial<WelderFilters>) {
  const params = convertToURLSearchParams(filters);
  return api.getPaginated<WelderListView[]>(`williams/welders?${params}`);
}

export function getWelderLookup(search: string) {
  return api.get<Welder[]>(`williams/welders/lookup?search=${search}`);
}

export function getWelderById(id: string) {
  return api.get<Welder>(`williams/welders/${id}`);
}

export function getWelderPicture(id: string) {
  return api.get<WelderPicture>(`williams/welders/${id}/picture`);
}

export function saveWelder(welder: Welder) {
  return api.post<Welder>("williams/welders", welder);
}

export function validateWelder(welder: Welder) {
  return api.post<WelderValidation>("williams/welders/validate", welder);
}

export function togglePinned(id: string) {
  return api.post(`williams/welders/pin?id=${id}`);
}

export function deleteWelder(id: string) {
  return api.delete(`williams/welders/${id}`);
}

export function getExpirations(id: string, process: string, processType: string) {
  const params = convertToURLSearchParams({ process, processType });
  return api.post<WelderExpirations>(`williams/welders/${id}/expirations?${params}`);
}

export function getWelderAssociations(id: string) {
  return api.get<WelderAssociations>(`williams/welders/${id}/associations`);
}

export function getWelderMetrics(id: string) {
  return api.get<WelderMetrics>(`williams/welders/${id}/metrics`);
}

export function getWelderStatus(id: string, search: WelderStatusSearch) {
  const params = convertToURLSearchParams(search);
  return api.get<WelderQualificationStatus>(`williams/welders/${id}/status?${params}`);
}

export function getWelderProcessStatus(id: string) {
  return api.get<WelderProcess[]>(`williams/welders/${id}/process-status`);
}

export function getWelderStatusHistory(id: string) {
  return api.get<WelderStatusHistory[]>(`williams/welders/${id}/status-history`);
}

export function getWelderUpdates(id: string) {
  const params = convertToURLSearchParams({ welderId: id, take: 100 });
  return api.getPaginated<WelderUpdate[]>(`williams/welders/updates?${params}`);
}

export function revokeProcess(id: string, process: WelderProcess) {
  return api.post<WelderUpdate[]>(`williams/welders/${id}/revoke`, process);
}
